import { api } from '@/lib/api';

/**
 * A bejelentkezett user feature-jogai a `/session`-ből (a token `permissions` claimje + isPlatformAdmin).
 * A `can(perm)` a sidebar/oldal-láthatóság kapuja — platform-admin superuser-bypasszal. A valódi
 * adat-izolációt az Odin RLS/ReBAC adja; ez CSAK a UI megjelenítés.
 *
 * A `hasScope(scope)` a TOKEN SCOPE-jára néz (nem feature-jogra): néhány művelet Odin-oldali PLATFORM-scope-hoz
 * kötött (pl. a munkatárs-címtár életciklusa → `iam:provision`), és ilyenkor a feature-jog önmagában nem
 * elég — a gomb megjelenítése hamis affordancia lenne.
 *
 * ══ HÁROM ÁLLAPOT, NEM KETTŐ ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ MÉRT ÉLES TÜNET: a felhasználó „nyomkodta a rendszert", és a lapok hirtelen azt írták ki, hogy nincs
 * jogosultsága. A hook MINDEN nem-200 `/session` válaszra ÜRES jog-listát adott, `isLoading: false` mellett —
 * vagyis a „nem tudtuk megkérdezni" ugyanúgy nézett ki, mint a „megkérdeztük, és nincs jogod". Egy lejárt
 * access-token (15 percenként normális) vagy egy elérhetetlen Odin (deploy, fojtás) így kiürítette a
 * menüt, eltüntette a gombokat, és a lapok `gated` ágra futottak — pedig a munkamenet élt.
 *
 * Ez ugyanaz a hibaosztály, amit a `refreshSession` (`transient` ≠ `unauthorized`) és a `requirePermission`
 * route-guard már megoldott; ez volt a harmadik olyan hely, ahol még benne maradt.
 *
 * A HARMADIK ÁLLAPOT (`isUnknown`): a lekérdezés LEFUTOTT, de nincs használható jogkép. Ilyenkor a `can()`
 * **ENGED** — ugyanazzal az indoklással, amivel a route-guard is enged:
 *   • ez a réteg **UX-kapu, nem biztonsági fal**: a fal a BFF `@RequirePermissions` + az Odin RLS, ami
 *     továbbra is dönt (jogtalan kérés 403-at kap, adat nem szivárog),
 *   • egy eltüntetett menü vagy egy „nincs jogosultságod" HAMIS ÍTÉLET — azt állítja, hogy a szerver nemet
 *     mondott, holott meg sem tudtuk kérdezni,
 *   • a lap így a SAJÁT adat-hibáját mutatja „Újra" gombbal, ami igaz és javítható állapot.
 */

/**
 * EXACT-MATCH scope-ok: ezekre a `platform:admin` superuser-bypass NEM érvényes.
 *
 * MIÉRT (mérve): a platform-oldali helperek KÉT osztályt kezelnek másképp. A
 * `canProvisionIdentities` és a `canManagePortalAccess` a `platform:admin`-t IS elfogadja, a `canImpersonate`
 * és a `canSendEmail` viszont EXACT-MATCH — a platform-admin ezeket NEM implikálja. A kliens-oldali „mindent
 * látok" bypass így egy platform-adminnál AKTÍV megszemélyesítés-gombot mutatott, amit a szerver 403-cal
 * utasított el: pontosan az a hamis affordancia, amit a `hasScope` hivatott megelőzni.
 *
 * Új tétel ide akkor kerül, ha a platform-oldali helper is exact-match (ne a jogosultság „erősségéből"
 * következtess — a helper a szerződés).
 */
const EXACT_MATCH_SCOPES: ReadonlySet<string> = new Set(['iam:impersonate', 'email:send']);
export function usePermissions(): {
  isLoading: boolean;
  /**
   * A jogkép ISMERETLEN: a lekérdezés lefutott, de nem adott használható választ (lejárt token, fojtás,
   * elérhetetlen upstream). NEM azt jelenti, hogy nincs jog — azt, hogy nem tudtuk megkérdezni.
   */
  isUnknown: boolean;
  isPlatformAdmin: boolean;
  permissions: string[];
  can: (perm: string) => boolean;
  hasScope: (scope: string) => boolean;
} {
  const q = api.session.useQuery(['session'], {});
  const body = q.data?.status === 200 ? q.data.body : undefined;
  // ISMERETLEN: már nem tölt, de nincs 200-as törzsünk. (A ts-rest kliens a nem-2xx választ ADATKÉNT adja,
  // nem hibaként — ezért `isLoading` ilyenkor is `false`, és ezért kellett ez a külön állapot.)
  const isUnknown = !q.isLoading && body === undefined;
  const permissions = body?.permissions ?? [];
  const isPlatformAdmin = body?.isPlatformAdmin ?? false;
  // A `scope` claim szóközzel tagolt kulcs-lista (a JWT-konvenció) — üres/hiányzó → nincs scope.
  const scopes = (body?.scope ?? '').split(/\s+/).filter(Boolean);
  return {
    isLoading: q.isLoading,
    isUnknown,
    isPlatformAdmin,
    permissions,
    // ISMERETLEN jogképnél ENGED (l. a fájl fejlécét): a szerver marad a fal, a felület viszont nem állít
    // hamis ítéletet. Ismert jogképnél a viselkedés bitre a régi.
    can: (perm: string) => isUnknown || isPlatformAdmin || permissions.includes(perm),
    hasScope: (scope: string) => {
      if (isUnknown) return true;
      return EXACT_MATCH_SCOPES.has(scope)
        ? scopes.includes(scope)
        : isPlatformAdmin || scopes.includes(scope);
    },
  };
}
